/**
 * Piezas compartidas de Bibliotecas y Biblioteca (rediseño AI Studio, DESIGN.md §9): íconos por colección, panel
 * lateral derecho (variante B del prototipo, 392 px bajo la cabecera) y desplegable con cierre por clic fuera y Escape.
 */
import { ElementType, ReactNode, useEffect, useRef } from 'react';
import { FileText, Gavel, Globe, Scale, X } from 'lucide-react';
import { motion } from 'motion/react';
import type { CollectionKey } from '../../utils/institutions';
import { usePanelMotion } from '../chat/panes/RightPanes';

export const ICONO_COLECCION: Record<CollectionKey, ElementType> = {
  adm: FileText,
  jud: Gavel,
  ley: Scale,
  conv: Globe,
};

/** Nombre corto de cada colección (selector de alcance, búsquedas recientes, panel). */
export const CORTO_COLECCION: Record<CollectionKey, string> = {
  adm: 'Administrativa SII',
  jud: 'Sentencias TTA',
  ley: 'Leyes y normas',
  conv: 'Convenios',
};

export const ANCHO_PANEL = 392;

export const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600';

/** Panel derecho: desde lg empuja el contenido (Layout reserva ANCHO_PANEL); bajo lg cubre la pantalla. */
export function PanelLateral({ titulo, onClose, children, pie }: { titulo: ReactNode; onClose: () => void; children: ReactNode; pie?: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const { contenedor, contenido } = usePanelMotion();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Al abrir, el foco entra al panel; al cerrar vuelve a lo que lo abrió (DESIGN.md §3).
  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => { if (previo && document.contains(previo)) previo.focus(); };
  }, []);
  return (
    <motion.aside
      {...contenedor}
      ref={ref}
      tabIndex={-1}
      aria-label={typeof titulo === 'string' ? titulo : 'Panel lateral'}
      className="fixed z-30 inset-x-0 bottom-0 top-[85px] lg:left-auto lg:w-[392px] border-l border-slate-200/80 bg-white flex flex-col focus:outline-none"
    >
      <motion.div {...contenido} className="flex-1 overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[12.5px] font-medium text-slate-500">{titulo}</span>
          <button type="button" onClick={onClose} aria-label="Cerrar panel" title="Cerrar (Esc)" className={`p-1 text-slate-400 hover:text-slate-700 rounded ${FOCO}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {children}
      </motion.div>
      {pie && <motion.div {...contenido} className="p-4 border-t border-slate-100 bg-white">{pie}</motion.div>}
    </motion.aside>
  );
}

/** Contenedor de un menú desplegable: se cierra con clic fuera o Escape (y devuelve el foco al botón). */
export function Desplegable({ abierto, onClose, boton, children, className = '', derecha = false, rol }: {
  abierto: boolean; onClose: () => void; boton: ReactNode; children: ReactNode; className?: string; derecha?: boolean; rol?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation(); // cierra solo el menú, no el panel lateral (que escucha Escape en window)
      onClose();
      ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', tecla); };
  }, [abierto, onClose]);
  return (
    <div ref={ref} className="relative">
      {boton}
      {abierto && <div role={rol} className={`absolute ${derecha ? 'right-0' : 'left-0'} top-full mt-1.5 bg-white rounded-xl border border-slate-200 shadow-lg py-1.5 z-30 text-[12px] ${className}`}>{children}</div>}
    </div>
  );
}

/** Barra gris de relleno para el estado «cargando». */
export function Barra({ ancho = '60%', alto = 'h-3' }: { ancho?: string; alto?: string }) {
  return <span aria-hidden="true" className={`block ${alto} rounded bg-slate-100 animate-pulse motion-reduce:animate-none`} style={{ width: ancho }} />;
}
